import React, { useState, useEffect, useMemo } from 'react';
import AvatarEditor from 'react-avatar-editor';
import './BackgroundEditor.css';

const BackgroundEditor = (props) => {
    const { editor, fileObj, setFileObj, setDisableConfirm, permissions, themeType } = props;
    
    const [ position, setPosition ] = useState({x: 0.5, y: 0.5});
    const [ scale, setScale ] = useState(1);
    const [ message, setMessage ] = useState(null);
    // this motion is for showing the overlay to disable editing for mp4 and gif files (separate from fileObj.motion)
    const [ motion, setMotion ] = useState(false);
    const stillFiles = useMemo(() => ['image/png','image/jpeg','image/jpg'], []);
    const motionFiles = useMemo(() => ['video/mp4','image/gif'], []);

    useEffect(() => {
        if (fileObj.type.includes('mp4') || fileObj.type.includes('gif')) {
            setMotion(true);
            setMessage(<div>Motion File Selected</div>);
        } else if ((themeType === 'new' || themeType === 'default') && (typeof fileObj.file === 'string')) {
            setMotion(false);
            setMessage(
                <>
                <div>No file selected.</div>
                <div>Please choose a file.</div>
                </>
            );
        } else {
            setMotion(false);
            setMessage(null);
        };
    },[fileObj, themeType]);

    const handlePhotoToEdit = (e) => {
        if (e.target.files[0]?.size < 10000000) {
            if (permissions.background === 1) {
                if (stillFiles.includes(e.target.files[0].type)) {
                    setFileObj(prev => ({
                        ...prev,
                        background_updated: true,
                        file: e.target.files[0],
                        motion: false,
                        type: e.target.files[0].type,
                    }));
                    setMessage(null);
                    setDisableConfirm(false);
                    setMotion(false);
                } else {
                    setMessage(
                        <>
                        <div>Invalid File Type</div>
                        <div>Acceptable Files: .png, .jpeg, .jpg</div>
                        </>
                    );
                    setDisableConfirm(true);
                };
            } else if (permissions.background === 2) {
                if (stillFiles.includes(e.target.files[0].type)) {
                    setFileObj(prev => ({
                        ...prev,
                        background_updated: true,
                        file: e.target.files[0],
                        motion: false,
                        type: e.target.files[0].type,
                    }));
                    setMessage(null);
                    setDisableConfirm(false);
                    setMotion(false);
                } else if (motionFiles.includes(e.target.files[0].type)) {
                    setFileObj(prev => ({
                        ...prev,
                        background_updated: true,
                        file: e.target.files[0],
                        type: e.target.files[0].type,
                        motion: e.target.files[0].type.includes('video/mp4'),
                    }));
                    setMotion(true);
                    setMessage(
                        <div>Motion File Selected</div>
                    );
                    setDisableConfirm(false);
                } else {
                    setMessage(
                        <>
                        <div>Invalid File Type</div>
                        <div>Acceptable Files: .png, .jpeg, .jpg, .mp4, .gif</div>
                        </>
                    );
                    setDisableConfirm(true);
                };
            };
        } else if (e.target.files[0]?.size > 10000000) {
            setDisableConfirm(true);
            setMessage(
                <>
                <div>File is too large.</div>
                <div>Please choose another file.</div>
                </>
            );
        };
    };

    const handleScale = (e) => {
        if (permissions.background === 1 || permissions.background === 2) {
            const scale = parseFloat(e.target.value);
            setScale(scale);
            setFileObj(prev => ({
                ...prev,
                background_updated: true
            }));
        };
    };

    const handlePositionChange = (position) => {
        if (permissions.background === 1 || permissions.background === 2) {
            setPosition(position);
            setFileObj(prev => ({
                ...prev,
                background_updated: true
            }));
        };
    };

    // handles the preview
    useEffect(() => {
        let timeout;
        if (fileObj.background_updated && (editor.current || fileObj.file)) {
            if (stillFiles.includes(fileObj.type)) {
                timeout = setTimeout(async() => {
                    await editor.current.getImageScaledToCanvas().toBlob(blob => setFileObj(prev => ({
                        ...prev,
                        preview: URL.createObjectURL(blob),
                    })));
                }, 200);
            } else if (motionFiles.includes(fileObj.type)) {
                if (typeof fileObj.file === 'string' && fileObj.type === 'image/gif') {
                    // gif file loaded (not chosen)
                    fetch(fileObj.file).then(res => {
                        res.blob().then(blob => {
                            const gifPreview = URL.createObjectURL(blob);
                            setFileObj(prev => ({
                                ...prev,
                                preview: gifPreview,
                            }))
                        }).catch(err => console.log(err));
                    });
                } else if (typeof fileObj.file === 'string' && fileObj.type === 'video/mp4') {
                    // mp4 file loaded (not chosen)
                    setFileObj(prev => ({
                        ...prev,
                        preview: fileObj.file,
                    }));
                } else if (fileObj.type === 'image/gif' || fileObj.type === 'video/mp4') {
                    // gif/mp4 file chosen
                    const blob = new Blob([fileObj.file], {type: fileObj.type});
                    const vidPreview = URL.createObjectURL(blob);
                    setFileObj(prev => ({
                        ...prev,
                        preview: vidPreview,
                    }));
                };
            };
        };

        return () => timeout && clearTimeout(timeout);
    }, [scale, position, editor, fileObj.file, fileObj.type, stillFiles, motionFiles]);

    useEffect(() => {
        let moved = false;
        const editor = document.querySelector('.background-editor');
        const input = document.getElementById('background-pic-img');

        const downListener = () => moved = false;
        const moveListener = () => moved = true;
        const handleCanvasClick = (e) => (e.target === editor) && input.click();
        const upListener = (e) => (!moved) && handleCanvasClick(e);

        window.addEventListener('mousedown', downListener);
        window.addEventListener('mousemove', moveListener);
        window.addEventListener('mouseup', upListener);

        return () => {
            window.removeEventListener('mousedown', downListener);
            window.removeEventListener('mousemove', moveListener);
            window.removeEventListener('mouseup', upListener);
        };
    },[]);

    return (
        <div className='background-edit-wrap'>
            <label htmlFor='background-editor' className='bg-edit-label mb-0'>
                Background Editor
                <span className='bg-edit-label-inner'>Optimal Size: 1000 x 333</span>
            </label>
            <div className='avatar-wrap'>
                <AvatarEditor
                    className='background-editor'
                    id='background-editor'
                    ref={editor}
                    crossOrigin={'anonymous'}
                    borderRadius={0}
                    border={25}
                    image={fileObj.file}
                    scale={scale}
                    width={1000}
                    height={333}
                    position={position}
                    onPositionChange={handlePositionChange}
                    color={[0,0,0,0.4]}
                />
            </div>
            <div className='background-pic-img-wrap'>
                <input
                    type='file'
                    name='backgroundImg'
                    id='background-pic-img'
                    onChange={handlePhotoToEdit}
                    accept={permissions.background === 1 ? 'image/png, image/jpg, image/jpeg' : permissions.background === 2 ? 'image/png, image/jpg, image/jpeg, video/mp4, image/gif' : null}
                    disabled={permissions.background === 0}    
                />
                <label htmlFor='background-pic-img' className='background-pic-img-btn'>Choose File</label>
                {typeof fileObj.file === 'object' ? <div className='background-pic-img-label'>File Chosen: <span style={{fontWeight: 'bold'}}>{fileObj.file.name}</span></div> : null}
            </div>
            <div className='range-slider mb-3'>
                <label htmlFor='custom-range' className='form-label mb-0'>Zoom</label>
                <input  
                    type='range' 
                    className='form-range' 
                    id='custom-range' 
                    min='0.5'
                    max='3'
                    step='0.25'
                    value={scale}
                    onChange={handleScale}
                    disabled={permissions.background === 0 || motion}
                />
            </div>
            {permissions.background === 0 ?
                <div className='bg-overlay-files'>
                    Not permitted to edit background.
                </div>
            : (message) ?
                <div className='bg-overlay-files'>
                    {message}
                </div>
            : <></>
            } 
        </div>   
    )
}

export default BackgroundEditor;